import React, { useLayoutEffect, useState } from 'react';
import './meetings-game.css';
import {
    goBack,
    goToScreen,
    goToScreenAndReset,
    setHistoryScreenState,
} from '../../actions/router';
import { connect } from 'react-redux';
import {
    MAIN_GAME_SCREEN,
    MEETINGS_GAME_SCREEN,
    SETTINGS_SCREEN,
} from '../../constants/routes';
import { IconButton } from '../../components/icon-button';
import { Panel } from '../../components/panel';
import { Heading } from '../../components/heading';
import Copy from '../../copy/meetings';
import { completeTutorial } from '../../actions/tutorials';
import {
    completeMeeting,
    progressTime,
    updateOverallScores,
} from '../../actions/active-game';
import { Meeting } from './meeting';
import { MeetingPreview } from './meeting-preview';
import { MeetingAnimationScreen } from './meeting-animation';
import classNames from 'classnames';
import { TutorialModalParent } from '../../components/modal-parents/tutorial-modal';
import { ProgressUpdateModalParent } from '../../components/modal-parents/progress-update-modal';

const MeetingsGameScreen = props => {
    const {
        goBack,
        goToSettings,
        continueTo,
        continueRoute,
        historyScreenState,
        tutorialCompleted,
        completeMeetingsTutorial,
        previousScores,
        submitOverallScores,
        difficulty,
        currentMeetings,
        progressGame,
    } = props;

    const [activeMeeting, setActiveMeeting] = useState(null);
    const [step, setStep] = useState('selection');

    const [currentGameScores, setCurrentGameScores] = useState({});
    const [progressUpdateVisible, setProgressUpdateVisible] = useState(false);
    const [responseFeedback, setResponseFeedback] = useState(null);

    const modalVisible = progressUpdateVisible || !tutorialCompleted;

    useLayoutEffect(() => {
        if (historyScreenState.screen === MEETINGS_GAME_SCREEN) {
            setActiveMeeting(historyScreenState.state.activeMeeting);
            setStep(historyScreenState.state.step);
        }
    }, [historyScreenState]);

    const submitResponse = decision => {
        const scores = {
            party: activeMeeting[decision].score_party,
            voters: activeMeeting[decision].score_local,
            media: activeMeeting[decision].score_media,
        };
        setCurrentGameScores(scores);
        setResponseFeedback(activeMeeting[decision].feedback);
        if (decision === 'attend') {
            setStep('animation');
        } else {
            setProgressUpdateVisible(true);
        }
    };

    const completeGame = () => {
        if (continueRoute === MAIN_GAME_SCREEN) {
            submitOverallScores(
                previousScores,
                currentGameScores,
                activeMeeting.index,
            );
            progressGame();
        }
        continueTo(continueRoute);
    };

    const meetingsPageClasses = classNames({
        'meetings-game': true,
        'meetings-game--hidden': modalVisible,
    });

    const activeMeetings = currentMeetings.map(i => {
        return { ...Copy.meetings[difficulty][i], index: i };
    });

    return (
        <>
            <div className={meetingsPageClasses} aria-hidden={modalVisible}>
                <IconButton
                    iconName="back"
                    size="small"
                    type="essential"
                    withSpacing={true}
                    onClick={() =>
                        step === 'selection'
                            ? goBack()
                            : step === 'meeting'
                            ? setStep('selection')
                            : setStep('meeting')
                    }
                    isDisabled={modalVisible || step === 'animation'}
                />
                <Panel
                    flexDirection="column"
                    withPadding={false}
                    color="purple"
                    currentPage={`meetings game ${step} `}
                    key={step}
                >
                    <div>
                        {step !== 'animation' && (
                            <Heading
                                headingType="h3"
                                colorScheme="dark"
                                withSpacing={true}
                            >
                                {Copy.heading[step]}
                            </Heading>
                        )}
                    </div>
                    {step === 'selection' ? (
                        <div className="meetings-game__meetings">
                            {activeMeetings.map(meeting => {
                                return (
                                    <MeetingPreview
                                        key={meeting.index}
                                        title={meeting.title}
                                        attendee={meeting.attendee}
                                        meetingIcon={meeting.meetingIcon}
                                        meetingLocation={
                                            meeting.meetingLocation
                                        }
                                        onClick={() => {
                                            setActiveMeeting(meeting);
                                            setStep('meeting');
                                        }}
                                        modalVisible={modalVisible}
                                    />
                                );
                            })}
                        </div>
                    ) : step === 'meeting' ? (
                        <Meeting
                            title={activeMeeting.title}
                            description={activeMeeting.description}
                            onClick={submitResponse}
                        />
                    ) : (
                        <MeetingAnimationScreen
                            meetingDuration={activeMeeting.meetingDuration ?? 4}
                            completeAnimation={() => {
                                setProgressUpdateVisible(true);
                            }}
                        />
                    )}
                </Panel>
                <IconButton
                    iconName="settings"
                    size="small"
                    type="essential"
                    withSpacing={true}
                    onClick={() => goToSettings(activeMeeting, step)}
                    isDisabled={modalVisible || step === 'animation'}
                />
            </div>
            {!tutorialCompleted && (
                <TutorialModalParent
                    screen="meetings"
                    onClick={() => completeMeetingsTutorial()}
                />
            )}
            {progressUpdateVisible === true && (
                <ProgressUpdateModalParent
                    previousScores={previousScores}
                    gameScores={currentGameScores}
                    feedback={responseFeedback}
                    onClick={() => completeGame()}
                />
            )}
        </>
    );
};

const mapStateToProps = state => {
    return {
        history: state.router.history,
        continueRoute: state.router.history[state.router.history.length - 2],
        historyScreenState: state.router.historyScreenState,
        tutorialCompleted:
            state.activeGame.tutorialsCompleted[MEETINGS_GAME_SCREEN],
        previousScores: state.activeGame.overallScores,
        currentMeetings: state.activeGame.currentMeetings,
        difficulty: state.settings.gameSettings.difficulty,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        goBack: () => {
            dispatch(goBack());
        },
        goToSettings: (activeMeeting, step) => {
            dispatch(
                setHistoryScreenState(MEETINGS_GAME_SCREEN, {
                    activeMeeting,
                    step,
                }),
            );
            dispatch(goToScreen(SETTINGS_SCREEN));
        },
        continueTo: route => {
            dispatch(goToScreenAndReset(route));
        },
        completeMeetingsTutorial: () => {
            dispatch(completeTutorial(MEETINGS_GAME_SCREEN));
        },
        submitOverallScores: (previousScores, gameScores, meetingIndex) => {
            dispatch(updateOverallScores(previousScores, gameScores));
            dispatch(completeMeeting(meetingIndex));
        },
        progressGame: () => {
            dispatch(progressTime('meetings'));
        },
    };
};

const connected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(MeetingsGameScreen);
export { connected as MeetingsGameScreen };
